type Callback<T> = (value: T, previous?: T) => unknown
type Subscription<T> = { callback: Callback<T>, active: boolean, cleanup?: unknown, revision: number }

// Keep callback identity and its resource owner together. Returned removers and
// remove(originalCallback) dispose the same record exactly once.
export function createSubscriptions<T>(onError: (error: unknown) => void) {
    const records: Subscription<T>[] = []
    const releaseCleanup = (cleanup: unknown) => {
        if (typeof cleanup === 'function') {
            try { cleanup() } catch (error) { onError(error) }
        }
    }
    const release = (record: Subscription<T>) => {
        const cleanup = record.cleanup
        record.cleanup = undefined
        releaseCleanup(cleanup)
    }
    const dispose = (record?: Subscription<T>) => {
        if (!record?.active) return
        record.active = false
        const index = records.indexOf(record)
        if (index !== -1) records.splice(index, 1)
        release(record)
    }
    return {
        get size() { return records.length },
        add: (callback: Callback<T>, initial?: { value: T }) => {
            const record: Subscription<T> = { callback, active: true, revision: 0 }
            // Preserve initialization before registration (including recursive
            // writes), while retaining the cleanup produced by that invocation.
            if (initial) record.cleanup = callback(initial.value)
            records.push(record)
            return () => dispose(record)
        },
        remove: (callback: Callback<T>) => dispose(records.find(record => record.callback === callback)),
        trigger: (value: T, previous?: T) => {
            for (const record of records.slice()) {
                if (!record.active) continue
                const revision = ++record.revision
                release(record)
                if (!record.active) continue
                try {
                    const cleanup = record.callback(value, previous)
                    if (record.active && revision === record.revision) record.cleanup = cleanup
                    else releaseCleanup(cleanup) // Removed/re-entered by its own callback.
                } catch (error) { onError(error) }
            }
        },
    }
}
